/**
 * THE SCHEME DRAFT — three places, three different jobs.
 *
 * The store's own note says a new slice "should be a decision taken on its own
 * merits rather than by drift", so here is the merit: authoring a scheme is a
 * long form an owner will not finish in one sitting. They will tab away to look
 * up a megawatt figure, navigate to check a standard, and close the laptop.
 * Losing the form to any of those is the difference between a tool and a toy.
 *
 * ══ THE RULE, AND IT IS THE HOUSE RULE ════════════════════════════════════
 *
 *     localStorage PROPOSES · Redux HOLDS · the server DISPOSES
 *
 * Exactly as `actingSlice` states it, because the problem has the same shape.
 *
 *   localStorage   what you typed, so a refresh or a crash does not lose it.
 *                  Never leaves the browser. Never authoritative.
 *
 *   Redux          the live form while you move between routes. No round trip
 *                  to come back to a half-filled form.
 *
 *   the server     the saved draft — `publish_state = 0`. The only version that
 *                  another person at your company can see, and the only one
 *                  that survives a different machine.
 *
 * ══ WHY THE RESTORE IS ANNOUNCED AND NOT SILENT ═══════════════════════════
 *
 * Silently repopulating a form is disorienting: you open Author a brief
 * expecting a blank page and find someone else's half-finished scheme, with no
 * idea where it came from or whether it is saved. So the page says what it
 * restored and offers to discard it. An unexplained form is worse than an empty
 * one.
 */

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/** Everything the form holds. Strings throughout — this is form state, not the
 *  API shape; conversion happens once, at submit. */
export interface SchemeDraft {
  name: string;
  facility: string;
  region: string;
  locality: string;
  load: string;
  rack: string;
  phasing: string;
  uptime: string;
  redundancy: string;
  discs: string[];
  stage: string;
  heads: string;
  value: string;
  start: string;
  weeks: string;
  closes: string;
  accreds: string[];
  /* WHO THIS SCHEME IS FOR — both off means the scheme is a private draft.
     Kept as separate flags rather than one mode, because a scheme can want
     design AND be ready to price at the same time. */
  wantsDesign: boolean;
  wantsMarket: boolean;
  designScope: string;
  /** Which tiers may FIND the package. Only meaningful when wantsMarket. */
  audience: string[];
}

export const EMPTY_DRAFT: SchemeDraft = {
  name: "", facility: "Hyperscale data centre", region: "", locality: "",
  load: "", rack: "", phasing: "", uptime: "Tier III", redundancy: "",
  discs: [], stage: "", heads: "", value: "", start: "", weeks: "",
  closes: "", accreds: [],
  /* BOTH OFF. An owner opening this form has not yet decided whether the
     scheme needs a designer, a contractor, or neither today — so we do not
     decide for them, and neither block asks for anything until they say. */
  wantsDesign: false, wantsMarket: false, designScope: "", audience: [],
};

interface State {
  draft: SchemeDraft;
  /** The server id once saved. Null while the draft has never been saved. */
  savedId: string | null;
  /** ISO time of the last successful server save. */
  savedAt: string | null;
  /** True when this session's draft came back from localStorage rather than
   *  being typed. Drives the "we restored this" banner, then clears. */
  restored: boolean;
  dirty: boolean;
}

const KEY = "lc_scheme_draft_v1";

/* A stored draft is a PROPOSAL. It is read once at startup, and anything
   malformed is discarded without comment — a corrupt entry must never stop the
   screen loading. */
function readStored(): Partial<State> {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { draft?: SchemeDraft; savedId?: string | null };
    if (!parsed?.draft || typeof parsed.draft !== "object") return {};
    return {
      draft: { ...EMPTY_DRAFT, ...parsed.draft },
      savedId: parsed.savedId ?? null,
      restored: true,
      dirty: true,
    };
  } catch {
    return {};
  }
}

/* Wrapped because a browser in private mode, or with site data blocked, throws
   on write. Losing the convenience is acceptable; crashing the form is not. */
function persist(s: State) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ draft: s.draft, savedId: s.savedId }));
  } catch { /* no-op */ }
}

function wipe() {
  try { localStorage.removeItem(KEY); } catch { /* no-op */ }
}

const initialState: State = {
  draft: EMPTY_DRAFT,
  savedId: null,
  savedAt: null,
  restored: false,
  dirty: false,
  ...readStored(),
};

const slice = createSlice({
  name: "schemeDraft",
  initialState,
  reducers: {
    /** One field at a time — the form is controlled and this runs per keystroke. */
    setField(state, a: PayloadAction<{ k: keyof SchemeDraft; v: string | string[] | boolean }>) {
      // @ts-expect-error — the key/value pairing is guaranteed by the caller.
      state.draft[a.payload.k] = a.payload.v;
      state.dirty = true;
      state.restored = false;
      persist(state);
    },

    /** Loading a saved draft from the server. Server wins, so this is not dirty. */
    loadFromServer(state, a: PayloadAction<{ id: string; draft: SchemeDraft }>) {
      state.draft = a.payload.draft;
      state.savedId = a.payload.id;
      state.dirty = false;
      state.restored = false;
      persist(state);
    },

    markSaved(state, a: PayloadAction<{ id: string }>) {
      state.savedId = a.payload.id;
      state.savedAt = new Date().toISOString();
      state.dirty = false;
      persist(state);
    },

    /** Published, or deliberately discarded. Either way the local copy goes —
     *  a published scheme must not come back as an unsaved draft next visit. */
    clearDraft(state) {
      state.draft = EMPTY_DRAFT;
      state.savedId = null;
      state.savedAt = null;
      state.restored = false;
      state.dirty = false;
      wipe();
    },

    dismissRestored(state) { state.restored = false; },
  },
});

export const { setField, loadFromServer, markSaved, clearDraft, dismissRestored } = slice.actions;
export default slice.reducer;
